import {useContext} from "@mytour/platform";
import {
  type CreateScheduledReminderPayload,
  type PreviewReminderDatesPayload,
  PreviewReminderDatesPayloadSchema,
} from "@mytour/shared";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {reminderApiService} from "./reminderApi.service.js";

export const reminderKeysStore = {
  root: ["reminder"] as const,
  previewDates: (
    payload: PreviewReminderDatesPayload,
    companyId: string | undefined,
    userId: string | undefined
  ) => [...reminderKeysStore.root, "previewDates", companyId, userId, payload] as const,
  smsStatus: (companyId: string | undefined, userId: string | undefined) =>
    [...reminderKeysStore.root, "smsStatus", companyId, userId] as const,
  activeForCustomer: (customerId: string) =>
    [...reminderKeysStore.root, "activeForCustomer", customerId] as const,
};

/**
 * Le rappel `scheduled` en attente du client, ou `null` — au plus un à la fois (AC #1254).
 * `GET /reminder/customer/:id` est `@Roles("manager")` : un intervenant qui atteint cette
 * query (ex. fiche client depuis l'intervention) obtiendrait un 403 sans ce gate côté rôle.
 */
export function useActiveReminder(customerId: string | undefined) {
  const {context} = useContext();
  const {data, isLoading, isFetching, refetch, error} = useQuery({
    queryKey: [
      ...reminderKeysStore.activeForCustomer(customerId ?? ""),
      context?.companyId,
      context?.userId,
    ],
    queryFn: () => reminderApiService.findActiveForCustomer(customerId as string),
    enabled: Boolean(customerId) && context?.role === "manager",
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  return {
    reminder: data,
    reminderError: error,
    isReminderLoading: isLoading,
    isReminderFetching: isFetching,
    refetchReminder: refetch,
  };
}

export interface CreateReminderVariables {
  customerId: string;
  payload: CreateScheduledReminderPayload;
}

export function useCreateReminder() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: ({payload}: CreateReminderVariables) => reminderApiService.createReminder(payload),
    onSuccess(createdReminder, {customerId}) {
      queryClient.setQueriesData(
        {queryKey: reminderKeysStore.activeForCustomer(customerId)},
        createdReminder
      );
    },
    onError() {
      console.error("Failed to create reminder");
    },
  });
  return {createReminder: mutateAsync, isCreatingReminder: isPending};
}

export interface CancelReminderVariables {
  reminderId: string;
  customerId: string;
}

export function useCancelReminder() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: ({reminderId}: CancelReminderVariables) =>
      reminderApiService.cancelReminder(reminderId),
    onSuccess(_result, {customerId}) {
      queryClient.setQueriesData(
        {queryKey: reminderKeysStore.activeForCustomer(customerId)},
        null
      );
    },
    onError(error) {
      console.error("Failed to cancel reminder:", error);
    },
  });
  return {cancelReminder: mutateAsync, isCancellingReminder: isPending};
}

export function useReminderSmsStatus() {
  const {context} = useContext();
  const {data, isLoading, error} = useQuery({
    queryKey: reminderKeysStore.smsStatus(context?.companyId, context?.userId),
    queryFn: reminderApiService.getSmsStatus,
    enabled: Boolean(context?.companyId && context?.userId),
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
  });
  return {smsStatus: data, isSmsStatusLoading: isLoading, smsStatusError: error};
}

export function useReminderDatesPreview(payload: PreviewReminderDatesPayload) {
  const {context} = useContext();
  const enabled =
    context?.role === "manager" && PreviewReminderDatesPayloadSchema.safeParse(payload).success;
  const {data, isFetching, error} = useQuery({
    queryKey: reminderKeysStore.previewDates(payload, context?.companyId, context?.userId),
    queryFn: () => reminderApiService.previewDates(payload),
    enabled,
    staleTime: 0,
  });
  return {
    previewDates: enabled && !isFetching && !error ? data?.dates : undefined,
    isPreviewLoading: enabled && isFetching,
    previewError: enabled ? error : null,
  };
}
